import { motion } from "framer-motion";
import { Check, ArrowRight, HardHat, Home, Building2 } from "lucide-react";
import { useQuote } from "@/context/QuoteContext";
import { SERVICES, IMAGES } from "@/data/site";

const ICONS = { roofing: HardHat, renovation: Home, commercial: Building2 };
const IMG = { roofing: IMAGES.roofing, renovation: IMAGES.renovation, commercial: IMAGES.commercial };

export const Services = () => {
  const { openQuote } = useQuote();

  return (
    <section id="services" data-testid="services-section" className="py-16 sm:py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-red-700">What We Build</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-950">
            All Levels of Construction. <span className="text-red-700">No Job We Can't Handle.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            From a single leak to an entire community re-roof or commercial build, Templar Armor delivers
            premium craftsmanship at every scale.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.id];
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`service-card-${s.id}`}
                className="group relative rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={IMG[s.id]} alt={s.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2.5">
                    <div className="grid place-items-center h-10 w-10 rounded-lg bg-red-700 text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display font-bold text-xl text-white">{s.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 leading-relaxed">{s.blurb}</p>
                  <ul className="mt-4 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                        <Check className="h-4 w-4 text-red-700 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`service-quote-${s.id}`}
                    onClick={() => openQuote(s.title)}
                    className="mt-6 inline-flex items-center gap-1.5 font-bold text-red-700 hover:gap-3 transition-all"
                  >
                    Get a quote for {s.title} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
